import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';
import { firmsAPI } from '@/lib/api';

export function FirmsTab({ firms, firmDialogOpen, setFirmDialogOpen, editFirm, setEditFirm, newFirm, setNewFirm, logoFile, setLogoFile, EMPTY_FIRM, onSaveFirm, onEditFirm, onDeleteFirm }) {

  const addBankAccount = () => {
    const accounts = newFirm.bank_accounts || [];
    setNewFirm({ ...newFirm, bank_accounts: [...accounts, { id: '', bank_name: '', account_number: '', ifsc: '' }] });
  };

  const updateBankAccount = (idx, field, value) => {
    const accounts = [...(newFirm.bank_accounts || [])];
    accounts[idx] = { ...accounts[idx], [field]: value };
    setNewFirm({ ...newFirm, bank_accounts: accounts });
  };

  const removeBankAccount = (idx) => {
    const accounts = [...(newFirm.bank_accounts || [])];
    accounts.splice(idx, 1);
    setNewFirm({ ...newFirm, bank_accounts: accounts });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold">Manage Firms</h3>
        <Dialog open={firmDialogOpen} onOpenChange={(open) => { setFirmDialogOpen(open); if (!open) { setEditFirm(null); setNewFirm({ ...EMPTY_FIRM }); setLogoFile(null); } }}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="create-firm-button"><Plus className="h-4 w-4 mr-1" /> Add Firm</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editFirm ? 'Edit Firm' : 'Add Firm'}</DialogTitle></DialogHeader>
            <form onSubmit={onSaveFirm} className="space-y-4">
              <div><Label>Firm Name *</Label><Input value={newFirm.name} onChange={e => setNewFirm({ ...newFirm, name: e.target.value })} required data-testid="firm-name-input" /></div>
              <div><Label>Address</Label><Textarea value={newFirm.address} onChange={e => setNewFirm({ ...newFirm, address: e.target.value })} rows={2} data-testid="firm-address-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>GSTIN</Label><Input value={newFirm.gstin} onChange={e => setNewFirm({ ...newFirm, gstin: e.target.value })} data-testid="firm-gstin-input" /></div>
                <div><Label>Invoice Prefix</Label><Input value={newFirm.invoice_prefix} onChange={e => setNewFirm({ ...newFirm, invoice_prefix: e.target.value })} placeholder="e.g. GRW, TAX" data-testid="firm-invoice-prefix" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>PAN</Label><Input value={newFirm.pan} onChange={e => setNewFirm({ ...newFirm, pan: e.target.value })} data-testid="firm-pan-input" /></div>
                <div><Label>Email ID</Label><Input type="email" value={newFirm.email} onChange={e => setNewFirm({ ...newFirm, email: e.target.value })} data-testid="firm-email-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Mobile Number</Label><Input value={newFirm.phone} onChange={e => setNewFirm({ ...newFirm, phone: e.target.value })} data-testid="firm-phone-input" /></div>
                <div>
                  <Label>Logo</Label>
                  <Input type="file" accept="image/*" onChange={e => setLogoFile(e.target.files[0] || null)} className="text-xs" data-testid="firm-logo-input" />
                  {editFirm?.logo_path && !logoFile && <p className="text-[10px] text-green-600 mt-0.5">Logo already uploaded</p>}
                </div>
              </div>

              {/* Bank Accounts (multiple) */}
              <div className="border rounded-lg p-3 bg-muted/30 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold">Bank Accounts</h4>
                  <Button type="button" size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addBankAccount} data-testid="add-bank-account-btn">
                    <Plus className="h-3 w-3" /> Add Account
                  </Button>
                </div>
                {(newFirm.bank_accounts || []).length === 0 && (
                  <p className="text-xs text-muted-foreground">No bank accounts added. Click "Add Account" to add one.</p>
                )}
                {(newFirm.bank_accounts || []).map((ba, idx) => (
                  <div key={ba.id || `new-${idx}`} className="border rounded-md p-2.5 bg-background space-y-2" data-testid={`bank-account-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Account {idx + 1}</span>
                      <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => removeBankAccount(idx)} data-testid={`remove-bank-${idx}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                    <div><Label className="text-xs">Bank Name</Label><Input value={ba.bank_name} onChange={e => updateBankAccount(idx, 'bank_name', e.target.value)} placeholder="e.g. State Bank of India" data-testid={`bank-name-${idx}`} /></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs">Account Number</Label><Input value={ba.account_number} onChange={e => updateBankAccount(idx, 'account_number', e.target.value)} data-testid={`bank-acno-${idx}`} /></div>
                      <div><Label className="text-xs">IFSC Code</Label><Input value={ba.ifsc || ''} onChange={e => updateBankAccount(idx, 'ifsc', e.target.value)} data-testid={`bank-ifsc-${idx}`} /></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Legacy single bank field (hidden, for backward compat with PDF) */}
              <input type="hidden" value={newFirm.bank_name || ''} />
              <input type="hidden" value={newFirm.bank_ifsc || ''} />
              <input type="hidden" value={newFirm.bank_account || ''} />

              <div className="grid grid-cols-2 gap-4">
                <div><Label>MSME Number</Label><Input value={newFirm.msme_number} onChange={e => setNewFirm({ ...newFirm, msme_number: e.target.value })} data-testid="firm-msme-input" /></div>
                <div><Label>CIN Number</Label><Input value={newFirm.cin_number} onChange={e => setNewFirm({ ...newFirm, cin_number: e.target.value })} data-testid="firm-cin-input" /></div>
              </div>
              <Button type="submit" className="w-full" data-testid="firm-submit-button">{editFirm ? 'Update Firm' : 'Add Firm'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      {firms.length === 0 ? (
        <Card><CardContent className="p-8 text-center"><Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-semibold mb-1">No firms</h3><p className="text-sm text-muted-foreground">Add your billing firms to link them to invoices.</p></CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {firms.map(f => (
            <Card key={f.id} data-testid={`firm-card-${f.id}`}>
              <CardContent className="p-4 flex items-start justify-between">
                <div className="flex gap-3">
                  {f.logo_path && (
                    <img src={firmsAPI.logoUrl(f.id)} alt="" className="w-10 h-10 rounded object-contain border" onError={e => e.target.style.display='none'} />
                  )}
                  <div className="space-y-1">
                    <h4 className="font-semibold text-sm">{f.name}</h4>
                    {f.address && <p className="text-xs text-muted-foreground">{f.address}</p>}
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      {f.gstin && <span>GSTIN: {f.gstin}</span>}
                      {f.pan && <span>PAN: {f.pan}</span>}
                      {f.invoice_prefix && <span>Invoice: {f.invoice_prefix}</span>}
                      {f.email && <span>{f.email}</span>}
                      {f.phone && <span>{f.phone}</span>}
                    </div>
                    {/* Bank accounts list */}
                    {(f.bank_accounts || []).length > 0 ? (
                      <div className="space-y-0.5">
                        {f.bank_accounts.map((ba, i) => (
                          <div key={ba.id || i} className="flex gap-x-3 text-xs text-muted-foreground">
                            <span>Bank: {ba.bank_name}</span>
                            <span>A/c: {ba.account_number}</span>
                            {ba.ifsc && <span>IFSC: {ba.ifsc}</span>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                        {f.bank_name && <span>Bank: {f.bank_name}</span>}
                        {f.bank_account && <span>A/c: {f.bank_account}</span>}
                        {f.bank_ifsc && <span>IFSC: {f.bank_ifsc}</span>}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      {f.msme_number && <span>MSME: {f.msme_number}</span>}
                      {f.cin_number && <span>CIN: {f.cin_number}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEditFirm(f)} data-testid={`firm-edit-${f.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => onDeleteFirm(f)} data-testid={`firm-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
